// MediScribe Constants

export const IMAGING_TYPES = [
  // MRI variants — contrast matters clinically, Genesis needs it in order
  "MRI w/o contrast", "MRI w/ contrast", "MRI w/ & w/o contrast", "MRI",
  // CT variants
  "CT w/o contrast", "CT w/ contrast", "CT w/ & w/o contrast", "CT Scan",
  "CT Low-Dose (LDCT)", "CT Sinus", "CT Calcium Score", "CT Angiography (CTA)",
  "X-Ray", "Ultrasound", "PET Scan", "Pulmonary Function Test (PFT)",
  "Mammography", "Fluoroscopy", "Nuclear Medicine", "Bone Scan",
  "Echocardiogram", "Angiography", "DEXA Scan",
  // Electrodiagnostic studies — ordered like imaging in Genesis
  "EMG (Electromyography)", "NCS (Nerve Conduction Study)", "EMG/NCS (Combined)",
  "EEG (Electroencephalogram)",
  "Other"
];

export const MEDICAL_SPECIALTIES = [
  "Other",
  "Continuity of Care (PCM Follow-up)",
  "Audiology", "Behavioral Health", "Cardiology", "Chiropractic", "Dermatology", "Electromyography (EMG/NCS Lab)", "Endocrinology",
  "Fertility/Reproductive Endocrinology", "Gastroenterology", "General Surgery", 
  "Gynecology", "Hematology", "Infectious Disease",
  "Nephrology", "Neurology", "Neuropsychology", "Nutrition/Dietetics", 
  "Obstetrics (OB)", "Obstetrics/Gynecology (OB/GYN)",
  "Occupational Therapy", "Oncology", "Ophthalmology", "Orthopedic Surgery",
  "Otolaryngology (ENT)", "Pain Management", "Physical Medicine & Rehab (PM&R)",
  "Physical Therapy", "Podiatry", "Psychiatry", "Psych-Trauma (PTSD/TBI)", "Pulmonology", 
  "Radiology/Diagnostic Imaging", "Rheumatology", "Sleep Medicine",
  "Speech Therapy", "Sports Medicine", "Urogynecology", "Urology", "Vascular Surgery"
];

export const BODY_PARTS = [
  // Head/Neck
  "Head/Brain", "Temporal Bones", "Sinuses", "Orbits", "IAC (Internal Auditory Canal)",
  "Face/Facial Bones", "Jaw/TMJ", "Neck/Soft Tissue", "Thyroid",
  // Spine
  "Cervical Spine", "Thoracic Spine", "Lumbar Spine", "Sacrum/Coccyx", "Entire Spine",
  // Upper Extremity - with directionality
  "Shoulder (Left)", "Shoulder (Right)", "Shoulder (Bilateral)",
  "Elbow (Left)", "Elbow (Right)", "Elbow (Bilateral)",
  "Wrist (Left)", "Wrist (Right)", "Wrist (Bilateral)",
  "Hand (Left)", "Hand (Right)", "Hand (Bilateral)",
  "Fingers (Left)", "Fingers (Right)", "Fingers (Bilateral)",
  // Lower Extremity - with directionality
  "Hip (Left)", "Hip (Right)", "Hip (Bilateral)",
  "Knee (Left)", "Knee (Right)", "Knee (Bilateral)",
  "Ankle (Left)", "Ankle (Right)", "Ankle (Bilateral)",
  "Foot (Left)", "Foot (Right)", "Foot (Bilateral)",
  "Toes (Left)", "Toes (Right)", "Toes (Bilateral)",
  // Torso
  "Chest", "Ribs (Left)", "Ribs (Right)",
  // Cardiac / cardiovascular imaging (echo, cardiac MR, stress test, calcium scoring locations)
  "Heart/Cardiac", "Coronary Arteries", "Cardiac (Stress)", "Cardiac (Resting)",
  "Cardiac MR (CMR)", "Cardiac CT", "Pericardium", "Great Vessels",
  "Abdomen", "Pelvis", "Abdomen/Pelvis",
  // GI / Solid organs
  "Liver", "Gallbladder", "Pancreas", "Spleen", "Kidneys", "Bladder",
  "Stomach", "Small Bowel", "Large Bowel/Colon", "Appendix",
  // Vascular
  "Carotid Arteries (Bilateral)", "Carotid Artery (Left)", "Carotid Artery (Right)",
  "Aorta (Abdominal)", "Aorta (Thoracic)",
  "Renal Arteries", "Renal Veins",
  "Lower Extremity Arteries (Bilateral)", "Lower Extremity Veins / DVT (Bilateral)",
  "Lower Extremity Veins / DVT (Left)", "Lower Extremity Veins / DVT (Right)",
  "Upper Extremity Veins / DVT (Left)", "Upper Extremity Veins / DVT (Right)",
  "Vertebral Arteries", "Subclavian Arteries",
  // Other
  "Other"
];

export const PATIENT_STATUSES = [
  { value: "active_duty", label: "Active Duty (AD/ADSM)" },
  { value: "active_guard", label: "Active Guard/AGR" },
  { value: "reserve", label: "Reserve/Guard (M-Day)" },
  { value: "veteran", label: "Veteran" },
  { value: "retiree", label: "Retiree" },
  { value: "dependent", label: "Dependent" },
  { value: "civilian", label: "Civilian" }
];

export const ENCOUNTER_TYPES = [
  { value: "new", label: "New Patient" },
  { value: "established", label: "Established Patient" }
];

export const VISIT_TYPES = [
  { value: "in_person_exam", label: "In-Person (with exam)" },
  { value: "in_person_counseling", label: "In-Person (counseling/med mgmt only)" },
  { value: "med_refill", label: "Med Renewal (Z76.0)" },
  { value: "results_review", label: "Results Review - In Person (Z71.2)" },
  { value: "results_review_virtual", label: "Results Review - Virtual (Z71.2)" },
  { value: "virtual", label: "Virtual/Telehealth" }
];

export const PANEL_OWNERSHIP = [
  { value: "my_patient", label: "My Patient" },
  { value: "other_provider", label: "Other Provider's" }
];

export const APPOINTMENT_TYPES = [
  { value: "follow_up", label: "F/U" },
  { value: "annual_wellness", label: "Annual Wellness" },
  { value: "counseling", label: "Counseling" },
  { value: "wellness", label: "Wellness" },
  { value: "chronic_mgmt", label: "Chronic Mgmt" },
  // Feb 27, 2026 — Per provider request: PHA and Acute removed,
  // "Annual Wellness" treated as a single appointment type.
];

// Feb 25, 2026 — reordered: "Other" stays first, then frequency-ranked based
// on user's actual clinic mix (high-volume sick call / MSK / refills first,
// admin/rare items at the bottom). Keeps the panel scrolling minimal for the
// 80/20 of common visits.
export const REASON_FOR_VISIT_OPTIONS = [
  { value: "other", label: "Other/Not specified" },
  { value: "sick_call", label: "Sick Call" },
  { value: "msk", label: "MSK (Musculoskeletal)" },
  { value: "medication_refill", label: "Medication Refill" },
  { value: "med_efficacy_eval", label: "Medication Efficacy Eval and Mgmt" },
  { value: "annual_exam", label: "Annual Wellness Exam" },
  { value: "annual_lab_review", label: "Annual Wellness Exam & Lab Results Review" },
  { value: "annual_health_promo", label: "Annual Wellness Exam & Health Promotion Referrals" },
  { value: "lab_review", label: "Lab Review" },
  { value: "imaging_review", label: "Imaging Review" },
  { value: "consult_follow_up", label: "Consult Follow-Up" },
  { value: "chronic_care", label: "Chronic Care Mgmt" },
  { value: "profile_eval", label: "Profile Evaluation" },
  { value: "bp_log_review", label: "BP Log Review" },
  { value: "adhd_med_mgmt", label: "ADHD Med Mgmt" },
  { value: "weight_med_mgmt", label: "Weight Med Mgmt and Counseling" },
  { value: "mental_health", label: "Mental Health" },
  { value: "deployment_health", label: "Deployment Health" },
  { value: "procedure", label: "Procedure" }
];

export const EM_CODES = [
  { value: "99214", label: "99214 - Moderate Complexity" },
  { value: "99215", label: "99215 - High Complexity" },
  { value: "99417", label: "+99417 - Extended Time (add-on)" }
];

export const LETTER_TYPES = [
  { value: "custom", label: "Custom Letter" },
  
  // === QUICK CONSULTS (NEW - Peer-to-Peer) ===
  { value: "portal_reply", label: "Reply to Portal Message", category: "Consult" },
  { value: "consult_behavioral_health", label: "Consult - Behavioral Health", category: "Consult" },
  { value: "consult_pharmacy", label: "Consult - Clinical Pharmacist", category: "Consult" },
  { value: "consult_specialist", label: "Consult - Specialty Referral", category: "Consult" },
  { value: "consult_peer_question", label: "Consult - Peer Question", category: "Consult" },
  { value: "consult_patient_handoff", label: "Consult - Patient Handoff", category: "Consult" },
  { value: "consult_medication_review", label: "Consult - Medication Review", category: "Consult" },
  
  // === RETURN TO WORK LETTERS ===
  { value: "return_to_work", label: "Return to Work (General)", category: "Return to Work" },
  { value: "return_to_work_restrictions", label: "Return to Work with Restrictions", category: "Return to Work" },
  { value: "return_to_work_mva", label: "Return to Work After MVA", category: "Return to Work" },
  { value: "return_to_work_mental_health", label: "Return to Work After Mental Health Leave", category: "Return to Work" },
  { value: "return_to_work_surgery", label: "Return to Work After Surgery", category: "Return to Work" },
  
  // === CIVILIAN ADMINISTRATIVE ===
  { value: "referral_request", label: "Referral Request / Issue", category: "Civilian" },
  { value: "jury_duty_excuse", label: "Jury Duty Medical Excuse", category: "Civilian" },
  { value: "school_504_accommodation", label: "School/College 504 Accommodation", category: "Civilian" },
  { value: "housing_accommodation", label: "Housing Accommodation (Fair Housing)", category: "Civilian" },
  { value: "parking_permit", label: "Disabled Parking Permit Certification", category: "Civilian" },
  { value: "std_claim_support", label: "Short-Term Disability Claim Support", category: "Civilian" },
  { value: "std_extension", label: "Short-Term Disability Extension", category: "Civilian" },
  // Feb 28, 2026 (v6.8.39) — SSA + LTD long-term disability application
  // helpers. Both are 1st-person patient-authored statements, NOT
  // provider letters. The SSA prompt walks the user through the 14
  // adult listings categories from
  // https://www.ssa.gov/disability/professionals/bluebook/AdultListings.htm
  // The LTD prompt is tuned for private LTD insurers (Lincoln Financial,
  // Cigna, Unum, etc.) and focuses on the "unable to perform material
  // duties of own occupation" standard during the elimination period.
  { value: "ssa_disability_statement", label: "SSA Disability — Functional Statement (1st-person, Bluebook-aligned)", category: "Civilian" },
  { value: "ltd_application_statement", label: "Long-Term Disability (LTD) Application — Personal Statement (1st-person)", category: "Civilian" },
  
  // === EXISTING WORKPLACE/FMLA ===
  { value: "letter_to_command", label: "Letter to Commander/Boss (AR 600-20)", category: "Workplace" },
  { value: "fmla_intermittent", label: "FMLA Intermittent Leave", category: "Workplace" },
  { value: "fmla_continuous", label: "FMLA Continuous Leave", category: "Workplace" },
  { value: "ada_accommodations", label: "ADA Workplace Accommodations", category: "Workplace" },
  
  // === MILITARY-SPECIFIC ===
  { value: "deployment_deletion", label: "Deployment Orders Deletion", category: "Military" },
  { value: "medication_waiver", label: "Medication Waiver (Approve Deploy)", category: "Military" },
  { value: "failed_clearance", label: "Failed Clearance / Deferral→Delete", category: "Military" },
  { value: "medical_stabilization", label: "Medical Stabilization (PCS Hold)", category: "Military" },
  { value: "weight_exception", label: "ABCP Weight Exception", category: "Military" },
  { value: "profile_conversion", label: "Profile Conversion (Temp→Perm)", category: "Military" },
  { value: "meb_support", label: "MEB/PEB Support", category: "Military" },
  { value: "fitness_exception", label: "PT/AFT Exception", category: "Military" },
  { value: "med_disqualification", label: "Med Disqualification/Waiver", category: "Military" },
  { value: "travel_medications", label: "Travel with Medications", category: "Military" },
  { value: "provider_consult", label: "Provider-to-Provider (Profile Validation)", category: "Military" },
  { value: "pregnancy_loss_leave", label: "Pregnancy Loss Convalescent Leave (AD 2025-02)", category: "Military" },
  { value: "care_plan_5305", label: "Care Plan Support (DA Form 5305)", category: "Military" },

  // === IDES APPEAL (Feb 22, 2026) — soldier or witness signed ===
  { value: "ides_appeal_soldier", label: "IDES Rebuttal / Appeal (1st person, soldier-signed)", category: "IDES Appeal" },
  { value: "buddy_statement", label: "Character / Buddy Statement (witness-signed)", category: "IDES Appeal" },

  // === VA APPEAL (Mar 3, 2026) — Veteran-signed disability appeal.
  // Distinct from IDES (active-duty MEB/PEB): this covers Notice of
  // Disagreement, Higher-Level Review, Supplemental Claim, and Board
  // of Veterans' Appeals (BVA) direct-review docket paths under
  // AMA/RAMP-era VA benefits law (38 CFR 3.2500 etc). 1st-person voice.
  { value: "va_claim_appeal", label: "VA Claim Appeal (NOD / HLR / Supplemental / BVA — 1st person)", category: "IDES Appeal" },
  { value: "va_nexus_letter_request", label: "VA Nexus Letter — Request Your Provider to Author", category: "IDES Appeal" }
];

// === PORTAL MESSAGE SCENARIOS (Patient & Civilian) ===
export const MESSAGE_SCENARIOS = [
  // Medication
  { value: 'refill_same_pharmacy', label: 'Medication Refill (Same Pharmacy)', category: 'Medication',
    prompt: 'Which medication needs refilled? How long have you been on it? Any changes in symptoms?',
    example: 'I need a refill on my [medication name, dose]. I have been taking it for [duration] with no issues. My pharmacy is [pharmacy name & location].' },
  { value: 'refill_different_pharmacy', label: 'Refill to Different Pharmacy', category: 'Medication',
    prompt: 'Which medication? Why a different pharmacy? (traveling, moved, closer location)',
    example: 'I need my [medication] refilled but sent to [new pharmacy name & city] because I am [out of town / relocated / closer to work]. My usual pharmacy is [old pharmacy].' },
  { value: 'outside_prescription', label: 'Outside Provider Prescription', category: 'Medication',
    prompt: 'What was prescribed? By which provider/specialty? Do you need us to take over refills?',
    example: 'I was recently prescribed [medication, dose] by my [outside psychiatrist / specialist / ER visit] on [date]. I need my PCM to [continue prescribing / bridge refill / add to my med list]. The prescribing provider was [name] at [clinic].' },
  { value: 'medication_side_effects', label: 'Medication Side Effects / Concerns', category: 'Medication',
    prompt: 'Which medication? What side effects? When did they start? How severe?',
    example: 'Since starting [medication] on [date], I have been experiencing [side effects]. It is [mild/moderate/severe] and [is/is not] affecting my daily duties. I would like to discuss [dose adjustment / alternative medication / stopping].' },
  { value: 'medication_prior_auth', label: 'Medication Needs Prior Authorization', category: 'Medication',
    prompt: 'Which medication was denied? What did the pharmacy say? Have you tried alternatives?',
    example: 'The pharmacy informed me that [medication] requires prior authorization. I have been on this medication for [duration] and previously tried [alternatives that did not work]. Please submit a prior auth so I can continue treatment.' },

  // Appointments & Follow-ups
  { value: 'routine_followup', label: 'Routine Follow-Up Request', category: 'Appointment',
    prompt: 'What condition needs follow-up? When was your last visit? Any changes since then?',
    example: 'I need to schedule a follow-up for [condition]. My last appointment was [date]. Since then, [symptoms improved / no change / worsened]. I am available [days/times].' },
  { value: 'urgent_concern', label: 'New or Worsening Symptom', category: 'Appointment',
    prompt: 'What symptom? When did it start or worsen? What have you tried? Need to be seen today?',
    example: 'I am experiencing [symptom] that started [when]. I have tried [self-care measures]. It is [getting worse / not improving]. I would like to know if I need to be seen in person or if there is something else I can try.' },
  { value: 'specialist_referral_status', label: 'Referral Status Check', category: 'Appointment',
    prompt: 'Which specialty referral? When was it placed? Have you been contacted by the specialist?',
    example: 'I am checking on the status of my referral to [specialty] that was placed on [date]. I have [not been contacted / been waiting X weeks / need to reschedule]. Please advise on next steps.' },

  // Results & Records
  { value: 'lab_results', label: 'Lab / Test Results Question', category: 'Results',
    prompt: 'Which labs or tests? When were they done? Do you have questions about specific results?',
    example: 'I had [labs / imaging / test] done on [date] and would like to know my results. Specifically, I am concerned about [specific test or value if known].' },
  { value: 'records_request', label: 'Medical Records Request', category: 'Results',
    prompt: 'What records do you need? For what purpose? Where should they be sent?',
    example: 'I need copies of my [medical records / lab results / imaging] from [date range] for [purpose: new provider, VA claim, legal]. Please send them to [destination or I will pick up].' },

  // Profile & Duty
  { value: 'profile_renewal', label: 'Profile Renewal / Extension', category: 'Profile & Duty',
    prompt: 'Which profile? When does it expire? Has your condition changed?',
    example: 'My temporary profile for [condition] expires on [date]. My condition [has not changed / has improved / has worsened]. I would like to request [renewal / extension / re-evaluation]. My current restrictions include [list restrictions].' },
  { value: 'profile_restriction_question', label: 'Profile Restriction Question', category: 'Profile & Duty',
    prompt: 'What restriction is unclear? What does your unit need clarified?',
    example: 'My unit is asking for clarification on my profile restriction regarding [specific restriction]. They need to know if I can [specific activity]. My profile states [current language]. Can you clarify or update this?' },
  { value: 'duty_limitation_letter', label: 'Need Duty Limitation Documentation', category: 'Profile & Duty',
    prompt: 'What limitation? For how long? What triggered this need?',
    example: 'I need documentation for a duty limitation due to [condition/medication change]. Specifically, I need [no formations before 0800 / limited duty hours / no field exercises] for [duration]. This is related to [medication change / new diagnosis / ongoing treatment].' },

  // Administrative
  { value: 'form_completion', label: 'Form Needs Provider Signature', category: 'Administrative',
    prompt: 'Which form? What is it for? When is it due?',
    example: 'I need my provider to review and sign [form name/number] for [purpose]. The deadline is [date]. I have [attached it to this message / will bring it to my appointment / uploaded it to my records].' },
  { value: 'general_question', label: 'General Medical Question', category: 'Administrative',
    prompt: 'What is your question? Provide relevant background (diagnosis, medications, recent changes).',
    example: 'I have a question about [topic]. Background: I am currently [diagnosed with / taking / recovering from]. My question is [specific question]. I would like to know if I need to be seen or if this can be addressed via message.' },
];

export const MTF_OPTIONS = [
  { value: 'Moncrief Army Health Clinic, Fort Jackson, SC', label: 'Moncrief Army Health Clinic (Fort Jackson)' },
  { value: 'Eisenhower Army Medical Center, Fort Eisenhower, GA', label: 'Eisenhower Army Medical Center (Augusta)' },
  { value: 'Winn Army Community Hospital, Fort Stewart, GA', label: 'Winn Army Community Hospital (Fort Stewart)' },
  { value: 'Womack Army Medical Center, Fort Liberty, NC', label: 'Womack Army Medical Center (Fort Liberty/Bragg)' },
  { value: 'Martin Army Community Hospital, Fort Moore, GA', label: 'Martin Army Community Hospital (Fort Moore/Benning)' },
  { value: 'Blanchfield Army Community Hospital, Fort Campbell, KY', label: 'Blanchfield Army Community Hospital (Fort Campbell)' },
  { value: 'custom', label: 'Other (type below)' }
];

export const RANKS = [
  // Civilian Titles
  'Mr.','Mrs.','Ms.','Miss','Dr.',
  // Enlisted
  'PVT','PV2','PFC','SPC','CPL','SGT','SSG','SFC','MSG','1SG','SGM','CSM',
  // Officer
  '2LT','1LT','CPT','MAJ','LTC','COL','BG','MG','LTG','GEN'
];

export const MOOD_THEMES = [
  { id: 'coral', name: 'Steady', emoji: '\uD83D\uDE10', class: 'mood-coral', description: 'Getting it done', color: '#b86b4a' },
  { id: 'calm', name: 'Calm', emoji: '\uD83D\uDE0C', class: 'mood-calm', description: 'Plushy indigo fleece', color: '#3a3a6a' },
  { id: 'turquoise', name: 'Deep Ocean', emoji: '\uD83D\uDC8E', class: 'mood-turquoise', description: 'Gemstone turquoise', color: '#0077a0' },
  { id: 'energized', name: 'Locked In', emoji: '\uD83D\uDE24', class: 'mood-energized', description: 'Saddle leather focus', color: '#6a4a2a' },
  { id: 'furry', name: 'Dainty', emoji: '\uD83D\uDC85', class: 'mood-furry', description: 'Furry pink rug', color: '#e8a4b8' },
  { id: 'night', name: 'Tired', emoji: '\uD83D\uDE34', class: 'mood-night', description: 'Running on fumes', color: '#1e2a3a' },
  { id: 'clinical', name: 'Neutral', emoji: '\uD83D\uDE36', class: 'mood-clinical', description: 'Professional mode', color: '#e8ddd0' },
  { id: 'recovery', name: 'Relieved', emoji: '\uD83D\uDE2E\u200D\uD83D\uDCA8', class: 'mood-recovery', description: 'Finally breathing', color: '#4a7a5a' },
  { id: 'hectic', name: 'Overwhelmed', emoji: '\uD83D\uDE30', class: 'mood-hectic', description: 'Too much at once', color: '#a34545' },
  { id: 'irritated', name: 'Over It', emoji: '\uD83D\uDE44', class: 'mood-irritated', description: 'Done with this', color: '#4a2020' },
  { id: 'melancholy', name: 'Low', emoji: '\uD83D\uDE14', class: 'mood-melancholy', description: 'Heavy day', color: '#3a3d4a' },
  // New pragmatic moods
  { id: 'empath', name: 'Empath', emoji: '\uD83E\uDEC2', class: 'mood-empath', description: 'Feeling everything', color: '#7a5a6a' },
  { id: 'robot', name: 'Robot Mode', emoji: '\uD83E\uDD16', class: 'mood-robot', description: 'Autopilot zombie', color: '#4a4a52' },
  { id: 'indifferent', name: 'Indifferent', emoji: '\uD83D\uDE11', class: 'mood-indifferent', description: 'Whatever happens', color: '#6a6a6a' },
  { id: 'content', name: 'Content', emoji: '\uD83D\uDE42', class: 'mood-content', description: "Nothing's wrong", color: '#8a9a7a' },
  { id: 'braced', name: 'Braced', emoji: '\uD83E\uDEE8', class: 'mood-braced', description: 'Waiting for impact', color: '#5a4a3a' },
  { id: 'cappuccino', name: 'Cappuccino', emoji: '\u2615', class: 'mood-cappuccino', description: 'Warm foam swirls', color: '#6b4423' },
  { id: 'hulk', name: 'Charged Up', emoji: '\uD83D\uDCAA', class: 'mood-hulk', description: 'Feeling powerful', color: '#1a3d1a' },
  { id: 'eggplant', name: 'Eggplant', emoji: '\uD83C\uDF46', class: 'mood-eggplant', description: 'Rich purple depth', color: '#3d1f56' },
  { id: 'sunset', name: 'Sunset', emoji: '\uD83C\uDF05', class: 'mood-sunset', description: 'Warm golden hour', color: '#c75b39' },
  { id: 'high-contrast', name: 'High Contrast', emoji: '\uD83D\uDC41\uFE0F', class: 'mood-high-contrast', description: 'Maximum readability', color: '#000000' },
];

export const PROFILE_RESTRICTIONS = [
  { 
    value: "concussion_mTBI", 
    label: "Head Injury Recovery",
    restrictions: [
      "No contact sports/combatives",
      "No driving/operating heavy machinery",
      "Limited screen time (30 min intervals, then break)",
      "Cognitive rest - limit complex tasks",
      "No weapons qualification",
      "No heights/climbing",
      "Gradual return to activity per provider clearance",
      "May need sunglasses indoors (light sensitivity)",
      "Quiet environment for work when possible"
    ],
    duration: "7-14 days typical"
  },
  { 
    value: "migraines", 
    label: "Recurrent Headaches",
    restrictions: [
      "Access to dark/quiet environment PRN",
      "Avoid triggers: bright lights, loud sustained noise",
      "Hydration breaks authorized (minimum 64oz/day)",
      "May need to step away from formation during acute episode",
      "Sunglasses authorized outdoors and indoors if needed",
      "Limited prolonged screen exposure",
      "Excused from gas chamber if triggers symptoms"
    ],
    duration: "30-90 days"
  },
  { 
    value: "behavioral_health", 
    label: "Behavioral Health Accommodation",
    restrictions: [
      "Flexible schedule for medical appointments",
      "No 24-hour duty (CQ/Staff Duty)",
      "Buddy system recommended for field exercises",
      "Low-stimulation work environment when possible",
      "May need breaks during high-stress situations",
      "Limited crowd exposure during recovery"
    ],
    duration: "30-90 days"
  },
  { 
    value: "sleep_disorder", 
    label: "Sleep-Related Accommodation",
    restrictions: [
      "Must have 8 consecutive hours of sleep opportunity in each 24-hour period",
      "No formation or duty assignment before 0800 for 90 days",
      "No 24-hour duty / CQ / Staff Duty",
      "No night driving",
      "No driving/operating heavy machinery until provider clears",
      "No operating weapons until stable on medication",
      "No duties requiring sustained alertness until adjusted",
      "Modified duty hours if medically indicated",
      "May require rest periods during duty day",
      "Buddy system for field exercises",
      "Report excessive drowsiness, dizziness, or cognitive slowing to provider",
      "No dose changes without re-evaluation of restrictions"
    ],
    duration: "90 days (if no dose changes)"
  },
  { 
    value: "medication_adjustment", 
    label: "Medication Adjustment Period",
    restrictions: [
      "No driving x 2 weeks or until provider clears",
      "No operating heavy machinery/weapons",
      "Monitor for side effects - report to provider",
      "Follow-up appointment within 2 weeks",
      "Limited duty during adjustment period",
      "Buddy system for field exercises"
    ],
    duration: "14-30 days"
  },
  { 
    value: "psychotropic_sedating", 
    label: "Sedating Psychotropic Medication (per OTSG 21-019)",
    restrictions: [
      "Must have 8 consecutive hours of sleep opportunity in each 24-hour period",
      "Excused from 0600 or earlier formations (sedation risk)",
      "No 24-hour duty / CQ / Staff Duty",
      "No night driving",
      "No operating heavy machinery or weapons until stable",
      "No duties requiring sustained alertness until adjusted",
      "Buddy system for field exercises",
      "Report excessive drowsiness, dizziness, or cognitive slowing to provider"
    ],
    duration: "30-90 days or ongoing"
  },
  { 
    value: "psychotropic_activating", 
    label: "Activating Psychotropic Medication (Stimulants/Antidepressants)",
    restrictions: [
      "Take medication in AM to avoid sleep interference",
      "Monitor heart rate and blood pressure",
      "Report chest pain, palpitations, or excessive anxiety",
      "Hydration - minimum 64oz water daily",
      "No energy drinks or excessive caffeine",
      "Follow-up within 2 weeks for dose adjustment",
      "May need modified PT if heart rate elevated"
    ],
    duration: "30-90 days"
  },
  { 
    value: "trauma_recovery", 
    label: "Trauma Recovery Support",
    restrictions: [
      "Flexible schedule for appointments (medical, legal, counseling)",
      "Low-stimulation work environment",
      "SARC/SHARP/Chaplain access as needed",
      "Buddy system available if desired",
      "Safe reporting environment",
      "Limit exposure to triggering situations"
    ],
    duration: "30-90 days"
  },
  { 
    value: "upper_extremity", 
    label: "Upper Extremity Limitation",
    restrictions: [
      "No lifting > ___ lbs with affected arm (specify: 5/10/15/20)",
      "No pushing/pulling with affected arm",
      "No overhead work",
      "No carrying weapon on affected side",
      "Modified PT - lower body/cardio only",
      "No combatives/grappling",
      "No pull-ups/push-ups (substitute authorized)"
    ],
    duration: "14-90 days"
  },
  { 
    value: "lower_extremity", 
    label: "Lower Extremity Limitation",
    restrictions: [
      "No running/jumping/marching",
      "Walk at own pace/distance",
      "No standing > 30 minutes continuously",
      "No ruck march",
      "Profile run: walk or bike substitute",
      "Elevator use authorized",
      "No combatives/grappling",
      "No leg tuck - substitute authorized"
    ],
    duration: "14-90 days"
  },
  { 
    value: "back_limitation", 
    label: "Back/Spine Limitation",
    restrictions: [
      "No lifting > ___ lbs (specify: 10/15/20/25)",
      "No repetitive bending/twisting/stooping",
      "Sit/stand/stretch as needed",
      "No ruck march",
      "Profile walk/bike (no running)",
      "No combatives",
      "Ergonomic workstation/chair if available",
      "No deadlift - substitute authorized"
    ],
    duration: "14-90 days"
  },
  { 
    value: "pregnancy", 
    label: "Pregnancy Accommodations",
    restrictions: [
      "No APFT/ACFT (exempt per DA 705)",
      "No combatives",
      "No prone position activities",
      "No exposure to chemicals/fumes/hazmat",
      "Bathroom breaks PRN",
      "Hydration breaks PRN",
      "No lifting > 20 lbs after 20 weeks",
      "No field duty after 28 weeks (per unit SOP)",
      "Modified uniform authorized"
    ],
    duration: "Until 6 weeks postpartum"
  },
  { 
    value: "postpartum", 
    label: "Postpartum Recovery",
    restrictions: [
      "No APFT/ACFT x 180 days (AD 2025-02)",
      "Gradual return to PT per provider",
      "Lactation accommodations per DoD policy",
      "No 24-hour duty",
      "Modified duty hours for infant care if needed",
      "Private space for pumping/nursing"
    ],
    duration: "180 days per AD 2025-02"
  },
  { 
    value: "post_surgical", 
    label: "Post-Surgical Recovery",
    restrictions: [
      "No lifting > ___ lbs (per surgical team)",
      "No driving x ___ days/weeks (specify)",
      "Wound care per instructions",
      "Follow-up per surgical team",
      "Limited duty / light duty only",
      "No PT until cleared by surgeon",
      "No field duty until cleared"
    ],
    duration: "Varies - per surgical team"
  },
  { 
    value: "respiratory", 
    label: "Respiratory Accommodation",
    restrictions: [
      "No gas chamber/CS exposure",
      "Limit exposure to dust/smoke/fumes",
      "Self-pace during cardio activities",
      "Rest breaks PRN during exertion",
      "Indoor PT option during poor air quality",
      "Rescue inhaler accessible at all times"
    ],
    duration: "30-90 days or ongoing"
  },
  { 
    value: "cardiac", 
    label: "Cardiac Accommodation",
    restrictions: [
      "Self-pace during physical activity",
      "No maximal exertion activities",
      "Rest breaks PRN",
      "Monitor for symptoms (report chest pain, SOB, dizziness)",
      "Walk test only (no run) until cleared",
      "No ruck march until cleared",
      "Limit heat exposure/ensure hydration"
    ],
    duration: "Until cardiology clearance"
  }
];

// ICD-10 patterns for live detection
export const ICD10_PATTERNS = [
  // Musculoskeletal - Spine
  { pattern: /spondylolisthesis|spondy/gi, code: 'M43.16', desc: 'Spondylolisthesis, lumbar region', category: 'Spine' },
  { pattern: /spinal stenosis|stenosis.*(lumbar|spine)/gi, code: 'M48.06', desc: 'Spinal stenosis, lumbar region', category: 'Spine' },
  { pattern: /herniat.*(disc|disk)|disc herniation|bulging disc/gi, code: 'M51.26', desc: 'Lumbar disc degeneration', category: 'Spine' },
  { pattern: /radiculopathy|radiating.*(leg|pain)/gi, code: 'M54.16', desc: 'Radiculopathy, lumbar region', category: 'Spine' },
  { pattern: /sciatica/gi, code: 'M54.30', desc: 'Sciatica, unspecified side', category: 'Spine' },
  { pattern: /(low back|lower back|lbp|lumbar).*(pain|ache)/gi, code: 'M54.50', desc: 'Low back pain', category: 'Spine' },
  { pattern: /cervical.*(pain|strain|sprain)/gi, code: 'M54.2', desc: 'Cervicalgia', category: 'Spine' },
  { pattern: /neck pain/gi, code: 'M54.2', desc: 'Cervicalgia', category: 'Spine' },
  // Musculoskeletal - Hip
  { pattern: /(right|r).*(hip).*(pain)/gi, code: 'M25.551', desc: 'Pain in right hip', category: 'Hip' },
  { pattern: /(left|l).*(hip).*(pain)/gi, code: 'M25.552', desc: 'Pain in left hip', category: 'Hip' },
  { pattern: /bilateral.*(hip).*(pain)/gi, code: 'M25.559', desc: 'Pain in unspecified hip', category: 'Hip' },
  { pattern: /hip.*(pain|ache)/gi, code: 'M25.559', desc: 'Pain in hip', category: 'Hip' },
  // Musculoskeletal - Knee
  { pattern: /meniscus|meniscal.*(tear|injury)/gi, code: 'M23.20', desc: 'Derangement of meniscus', category: 'Knee' },
  { pattern: /acl.*(tear|rupture|injury)|anterior cruciate/gi, code: 'S83.51', desc: 'Sprain of ACL', category: 'Knee' },
  { pattern: /pcl|posterior cruciate/gi, code: 'S83.52', desc: 'Sprain of PCL', category: 'Knee' },
  { pattern: /patellofemoral|pfps|anterior knee pain/gi, code: 'M22.2X9', desc: 'Patellofemoral disorders', category: 'Knee' },
  { pattern: /chondromalacia/gi, code: 'M22.40', desc: 'Chondromalacia patellae', category: 'Knee' },
  { pattern: /(right|r).*(knee).*(pain)/gi, code: 'M25.561', desc: 'Pain in right knee', category: 'Knee' },
  { pattern: /(left|l).*(knee).*(pain)/gi, code: 'M25.562', desc: 'Pain in left knee', category: 'Knee' },
  { pattern: /bilateral.*(knee).*(pain)/gi, code: 'M25.569', desc: 'Pain in unspecified knee', category: 'Knee' },
  { pattern: /knee.*(pain|ache)/gi, code: 'M25.569', desc: 'Pain in knee', category: 'Knee' },
  // Musculoskeletal - Shoulder
  { pattern: /rotator cuff.*(tear|injury)/gi, code: 'M75.10', desc: 'Rotator cuff tear', category: 'Shoulder' },
  { pattern: /impingement|shoulder impingement/gi, code: 'M75.40', desc: 'Impingement syndrome of shoulder', category: 'Shoulder' },
  { pattern: /(right|r).*(shoulder).*(pain)/gi, code: 'M25.511', desc: 'Pain in right shoulder', category: 'Shoulder' },
  { pattern: /(left|l).*(shoulder).*(pain)/gi, code: 'M25.512', desc: 'Pain in left shoulder', category: 'Shoulder' },
  { pattern: /shoulder.*(pain|ache)/gi, code: 'M25.519', desc: 'Pain in shoulder', category: 'Shoulder' },
  // Musculoskeletal - Ankle/Foot
  { pattern: /plantar fasciitis/gi, code: 'M72.2', desc: 'Plantar fascial fibromatosis', category: 'Foot' },
  { pattern: /ankle.*(sprain|strain)/gi, code: 'S93.40', desc: 'Sprain of ankle', category: 'Ankle' },
  { pattern: /(right|r).*(ankle).*(pain)/gi, code: 'M25.571', desc: 'Pain in right ankle', category: 'Ankle' },
  { pattern: /(left|l).*(ankle).*(pain)/gi, code: 'M25.572', desc: 'Pain in left ankle', category: 'Ankle' },
  { pattern: /(right|r).*(foot).*(pain)/gi, code: 'M79.671', desc: 'Pain in right foot', category: 'Foot' },
  { pattern: /(left|l).*(foot).*(pain)/gi, code: 'M79.672', desc: 'Pain in left foot', category: 'Foot' },
  // Mental Health
  { pattern: /ptsd|post.?traumatic stress/gi, code: 'F43.10', desc: 'Post-traumatic stress disorder', category: 'Mental Health' },
  { pattern: /acute stress reaction|acute stress disorder/gi, code: 'F43.0', desc: 'Acute stress reaction', category: 'Mental Health' },
  { pattern: /major depress|mdd/gi, code: 'F32.1', desc: 'Major depressive disorder, moderate', category: 'Mental Health' },
  { pattern: /depression|depressed/gi, code: 'F32.9', desc: 'Major depressive disorder', category: 'Mental Health' },
  { pattern: /anxiety|gad|generalized anxiety/gi, code: 'F41.1', desc: 'Generalized anxiety disorder', category: 'Mental Health' },
  { pattern: /adjustment disorder/gi, code: 'F43.20', desc: 'Adjustment disorder', category: 'Mental Health' },
  { pattern: /adhd.*(combined|both)|attention.*(combined|both)/gi, code: 'F90.2', desc: 'ADHD, combined type', category: 'Mental Health' },
  { pattern: /adhd.*(hyper|impulsiv)|attention.*(hyper|impulsiv)/gi, code: 'F90.1', desc: 'ADHD, predominantly hyperactive-impulsive', category: 'Mental Health' },
  { pattern: /adhd.*(inattentive|inatten)|attention.*(inattentive|inatten|deficit)/gi, code: 'F90.0', desc: 'ADHD, predominantly inattentive type', category: 'Mental Health' },
  { pattern: /\badhd\b|attention deficit/gi, code: 'F90.9', desc: 'ADHD, unspecified', category: 'Mental Health' },
  { pattern: /insomnia|sleep.*(difficulty|trouble|problem)/gi, code: 'G47.00', desc: 'Insomnia', category: 'Sleep' },
  // Headaches
  { pattern: /migraine/gi, code: 'G43.909', desc: 'Migraine, unspecified', category: 'Neurological' },
  { pattern: /tension.*(headache|ha)/gi, code: 'G44.209', desc: 'Tension-type headache', category: 'Neurological' },
  { pattern: /headache|cephalgia/gi, code: 'R51.9', desc: 'Headache', category: 'Neurological' },
  { pattern: /tbi|traumatic brain/gi, code: 'S06.9X0A', desc: 'Traumatic brain injury', category: 'Neurological' },
  // GI
  { pattern: /gerd|acid reflux|heartburn/gi, code: 'K21.0', desc: 'GERD with esophagitis', category: 'GI' },
  { pattern: /ibs|irritable bowel/gi, code: 'K58.9', desc: 'Irritable bowel syndrome', category: 'GI' },
  { pattern: /constipation/gi, code: 'K59.00', desc: 'Constipation', category: 'GI' },
  // Respiratory
  { pattern: /asthma/gi, code: 'J45.20', desc: 'Mild intermittent asthma', category: 'Respiratory' },
  { pattern: /upper respiratory infect|acute uri|\buri\b/gi, code: 'J06.9', desc: 'Acute upper respiratory infection', category: 'Respiratory' },
  { pattern: /bronchitis/gi, code: 'J20.9', desc: 'Acute bronchitis', category: 'Respiratory' },
  // Cardiovascular
  { pattern: /hypertension|htn|high blood pressure/gi, code: 'I10', desc: 'Essential hypertension', category: 'Cardiovascular' },
  { pattern: /hyperlipidemia|high cholesterol/gi, code: 'E78.5', desc: 'Hyperlipidemia', category: 'Cardiovascular' },
  // Endocrine
  { pattern: /diabetes mellitus|diabetes|t2dm|type 2 diabet|type ii diabet/gi, code: 'E11.9', desc: 'Type 2 diabetes mellitus', category: 'Endocrine' },
  { pattern: /hypothyroid/gi, code: 'E03.9', desc: 'Hypothyroidism', category: 'Endocrine' },
  { pattern: /obesity|obese|bmi.*(3[0-9]|4[0-9])/gi, code: 'E66.9', desc: 'Obesity', category: 'Endocrine' },
  // Pregnancy related
  { pattern: /pregnancy|pregnant/gi, code: 'Z33.1', desc: 'Pregnant state', category: 'Pregnancy' },
  { pattern: /miscarriage|pregnancy loss|spontaneous abortion/gi, code: 'O03.9', desc: 'Spontaneous abortion', category: 'Pregnancy' },
  // Pain
  { pattern: /chronic pain/gi, code: 'G89.29', desc: 'Other chronic pain', category: 'Pain' },
  { pattern: /fibromyalgia/gi, code: 'M79.7', desc: 'Fibromyalgia', category: 'Pain' },
  // Administrative / Counseling
  { pattern: /admin(?:istrative)?\s*exam|pre.?employment|pre.?deployment/gi, code: 'Z02.89', desc: 'Administrative exam', category: 'Administrative' },
  { pattern: /results?\s*review|lab\s*review|imaging\s*review/gi, code: 'Z71.2', desc: 'Results review', category: 'Administrative' },
  { pattern: /counsel(?:ing|ed)(?!.*result)/gi, code: 'Z71.89', desc: 'Other specified counseling', category: 'Administrative' },
  { pattern: /weight\s*(?:loss|management|counsel)|dietary\s*counsel|obesity\s*counsel|Z71\.3/gi, code: 'Z71.3', desc: 'Dietary counseling and surveillance', category: 'Administrative' },
  { pattern: /workplace\s*(?:discord|conflict|stress|issue)|job\s*(?:stress|conflict|dissatisfaction)|occupational\s*(?:stress|problem)/gi, code: 'Z56.9', desc: 'Unspecified problems related to employment', category: 'Administrative' },
  { pattern: /wellness|annual\s*(?:exam|physical|check)/gi, code: 'Z00.00', desc: 'General adult medical exam', category: 'Administrative' },
  { pattern: /med(?:ication)?\s*(?:renewal|refill)/gi, code: 'Z76.0', desc: 'Med renewal encounter', category: 'Administrative' },
];
